import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHeart, FaRegHeart, FaShoppingCart } from 'react-icons/fa';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { purchaseProfile, fetchUserBalance } from '@/redux/thunks/marketplaceThunks';
import { LoadingSpinner } from '@/components';
import PurchaseModal from '@/app/marketplace/components/PurchaseModal';
import { ProfileListing } from '@/app/marketplace/components/ProfileCard';
import { toast } from 'react-toastify';
import api from '@/utils/api';
import { getMediaUrl } from '@/constants/media';
import { formatName, formatHandle, nameInitial } from '@/utils/formatName';
import './UserNFTProfiles.scss';

interface NFTProfile {
  id: string;
  nftProfileId: number;
  createdAt: string;
  creator: {
    userId: string;
    username: string;
    displayName: string;
    avatar: string | null;
  };
  owner: {
    userId: string;
    username: string;
    displayName: string;
    avatar: string | null;
  };
  price: number;
  klyxValue: number;
  purchaseCount: number;
  isOwnedByCreator: boolean;
  isFavorited?: boolean;
}

interface UserNFTProfilesProps {
  username: string;
  isOwnProfile: boolean;
}

const UserNFTProfiles: React.FC<UserNFTProfilesProps> = ({ username, isOwnProfile }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth?.user);
  const userBalance = useAppSelector((state) => state.marketplace?.userBalance || 0);

  const [profiles, setProfiles] = useState<NFTProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<ProfileListing | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [togglingFavId, setTogglingFavId] = useState<string | null>(null);
  const LIMIT = 12;

  // Fetch profiles
  const fetchProfiles = async (reset = false) => {
    try {
      setIsLoading(true);
      setError(null);
      const currentOffset = reset ? 0 : offset;

      const response = await api.get(`/profile/${username}/nft-profiles`, {
        params: { limit: LIMIT, offset: currentOffset }
      });

      if (reset) {
        setProfiles(response.data.profiles);
        setOffset(LIMIT);
      } else {
        setProfiles(prev => [...prev, ...response.data.profiles]);
        setOffset(prev => prev + LIMIT);
      }

      setHasMore(response.data.hasMore);
    } catch (err: any) {
      console.error('Failed to fetch NFT profiles:', err);
      setError('Failed to load profiles');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchProfiles(true);
  }, [username]);

  // Fetch user balance
  useEffect(() => {
    if (currentUser) {
      dispatch(fetchUserBalance());
    }
  }, [currentUser, dispatch]);

  // Handle load more
  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      fetchProfiles();
    }
  };

  // Handle buy button click
  const handleBuyClick = (e: React.MouseEvent, profile: NFTProfile) => {
    e.stopPropagation();

    // Convert NFTProfile to ProfileListing format
    const profileListing: ProfileListing = {
      id: profile.id,
      creatorId: profile.creator.userId,
      creatorUsername: profile.creator.username,
      creatorDisplayName: profile.creator.displayName,
      creatorAvatarUrl: profile.creator.avatar || undefined,
      ownerId: profile.owner.userId,
      ownerUsername: profile.owner.username,
      ownerDisplayName: profile.owner.displayName,
      ownerAvatarUrl: profile.owner.avatar || undefined,
      price: profile.price,
      klyxValue: profile.klyxValue,
      trendingScore: 0,
      purchaseCount: profile.purchaseCount,
      creatorLocation: null,
      badges: {
        verified: false,
        topProfile: false,
        trending: false,
      },
    };

    setSelectedProfile(profileListing);
  };

  // Handle purchase confirmation
  const handlePurchaseConfirm = async (profile: ProfileListing, customPrice: number) => {
    setIsPurchasing(true);
    try {
      await dispatch(purchaseProfile({
        profileId: profile.id,
        customPrice,
      })).unwrap();

      toast.success('Profile purchased successfully! 🎉');
      setSelectedProfile(null);

      // Refresh the profiles list
      fetchProfiles(true);
    } catch (error: any) {
      console.error('Purchase failed:', error);
      toast.error(error || 'Failed to purchase profile');
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleToggleFavorite = async (e: React.MouseEvent, nftProfileId: number, currentlyFavorited: boolean) => {
    e.stopPropagation();
    const key = String(nftProfileId);
    if (togglingFavId === key) return;
    setTogglingFavId(key);

    // Optimistic update
    setProfiles(prev =>
      prev.map(p => p.nftProfileId === nftProfileId ? { ...p, isFavorited: !currentlyFavorited } : p)
    );

    try {
      await api.post('/favorites/toggle', { profileId: nftProfileId });
      toast.success(currentlyFavorited ? 'Removed from favorites' : 'Added to favorites');
    } catch {
      // Revert on failure
      setProfiles(prev =>
        prev.map(p => p.nftProfileId === nftProfileId ? { ...p, isFavorited: currentlyFavorited } : p)
      );
      toast.error('Failed to update favorite');
    } finally {
      setTogglingFavId(null);
    }
  };

  // Navigate to the creator's profile page
  const handleProfileClick = (profile: NFTProfile) => {
    navigate(`/profile/@${profile.creator.username}`);
  };

  return (
    <div className="user-nft-profiles">
      {error && (
        <div className="profiles-error">
          <p>{error}</p>
          <button onClick={() => fetchProfiles(true)}>Try Again</button>
        </div>
      )}

      {isLoading && profiles.length === 0 ? (
        <LoadingSpinner size="medium" message="Loading profiles..." />
      ) : profiles.length === 0 ? (
        <div className="profiles-empty">
          <p>{isOwnProfile ? "You don't own any profiles yet." : "No profiles owned yet."}</p>
          {isOwnProfile && (
            <button
              className="btn-create"
              onClick={() => navigate('/create-nft-profile')}
            >
              Create Your First Profile
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="profiles-grid">
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className="profile-card"
                onClick={() => handleProfileClick(profile)}
              >
                {/* Avatar */}
                <div className="profile-avatar">
                  {profile.creator.avatar ? (
                    <img src={getMediaUrl(profile.creator.avatar) || undefined} alt={profile.creator.username} />
                  ) : (
                    <div className="avatar-placeholder">
                      {nameInitial({ displayName: profile.creator.displayName, username: profile.creator.username })}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="profile-info">
                  <h3 className="profile-name">{formatName({ displayName: profile.creator.displayName, username: profile.creator.username })}</h3>
                  <p className="profile-username">{formatHandle(profile.creator.username)}</p>

                  {/* Stats */}
                  <div className="profile-stats">
                    <div className="stat">
                      <span className="stat-value">
                        {profile.klyxValue > 0
                          ? profile.klyxValue.toLocaleString(undefined, { maximumFractionDigits: 0 })
                          : '—'}
                      </span>
                      <span className="stat-label">Value</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{profile.purchaseCount}</span>
                      <span className="stat-label">Trades</span>
                    </div>
                  </div>

                  {/* Price */}
                  {profile.price > 0 && (
                    <div className="profile-price">
                      {profile.price.toFixed(2)} KLV
                    </div>
                  )}
                </div>

                {/* Favorite button */}
                {profile.isFavorited !== undefined && (
                  <button
                    className={`btn-favorite ${profile.isFavorited ? 'favorited' : ''}`}
                    onClick={(e) => handleToggleFavorite(e, profile.nftProfileId, !!profile.isFavorited)}
                    disabled={togglingFavId === String(profile.nftProfileId)}
                    title={profile.isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {profile.isFavorited ? <FaHeart /> : <FaRegHeart />}
                  </button>
                )}

                {/* Buy Now button - only show if user doesn't own this profile and price > 0 */}
                {currentUser && currentUser.id !== profile.owner.userId && profile.price > 0 && (
                  <button
                    className="btn-buy-now"
                    onClick={(e) => handleBuyClick(e, profile)}
                    title="Buy this profile"
                  >
                    <FaShoppingCart /> Buy Now
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div className="profiles-load-more">
              <button
                onClick={handleLoadMore}
                disabled={isLoading}
                className="btn-load-more"
              >
                {isLoading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Purchase Modal */}
      {selectedProfile && (
        <PurchaseModal
          profile={selectedProfile}
          userBalance={userBalance}
          onClose={() => setSelectedProfile(null)}
          onConfirm={handlePurchaseConfirm}
          isProcessing={isPurchasing}
        />
      )}
    </div>
  );
};

export default UserNFTProfiles;
